import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  useColorMode,
  Link,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import React from 'react';

import { route } from 'nextjs-routes';

import useNavItems, { isGroupItem, isInternalItem } from 'lib/hooks/useNavItems';
import IconSvg from 'ui/shared/IconSvg';
import LanguageSwitcher from 'ui/snippets/topBar/LanguageSwitcher';

import NavLinkIcon from '../NavLinkIcon';
import NavLinkGroup from './NavLinkGroup';

interface Props {
  onNavLinkClick?: () => void;
  isMarketplaceAppPage?: boolean;
}

const NavigationMobile = ({ onNavLinkClick }: Props) => {
  const { mainNavItems } = useNavItems();
  const [ openedGroupIndex, setOpenedGroupIndex ] = React.useState(-1);
  const { colorMode, toggleColorMode } = useColorMode();

  const textColor = useColorModeValue('#2A2A2E', '#EFEFEF');
  const activeTextColor = '#14DCAC';
  const activeBg = useColorModeValue('#f0f0f0', '#222222');
  const dividerColor = useColorModeValue('#dddddd', '#222222');
  const madeWithColor = useColorModeValue('#666', '#999');
  const madeWithHoverColor = useColorModeValue('#000', '#fff');
  const blockscoutIconName = useColorModeValue('blockscout_black', 'blockscout');

  const toggleGroup = React.useCallback((index: number) => {
    setOpenedGroupIndex(prev => (prev === index ? -1 : index));
  }, []);

  const groupClickHandlers = React.useMemo(
    () => mainNavItems.map((_, i) => () => toggleGroup(i)),
    [ mainNavItems, toggleGroup ],
  );

  return (
    <Box display="flex" flexDirection="column" minH="100%">
      <VStack as="ul" spacing={ 0 } alignItems="stretch" w="100%">
        { mainNavItems.map((item, index) => {
          if (isGroupItem(item)) {
            const isExpanded = openedGroupIndex === index;
            return (
              <Box key={ item.text } as="li" listStyleType="none" w="100%">
                { /* 分组标题行 */ }
                <NavLinkGroup
                  item={ item }
                  onClick={ groupClickHandlers[index] }
                  isExpanded={ isExpanded }
                  activeBg={ activeBg }
                />

                { /* 内联展开的子项 */ }
                { isExpanded && (
                  <Box as="ul">
                    { item.subItems?.map((subItem) => {
                      const subList = Array.isArray(subItem) ? subItem : [ subItem ];
                      return subList.map((si) => {
                        const isInternal = isInternalItem(si);
                        const isSubActive = isInternal && si.isActive;
                        const href = isInternal ? route(si.nextRoute) : si.url;
                        const linkContent = (
                          <Link
                            href={ href }
                            display="flex"
                            alignItems="center"
                            h="48px"
                            pl={ 8 }
                            pr={ 4 }
                            gap="12px"
                            w="100%"
                            color={ isSubActive ? activeTextColor : textColor }
                            bg={ isSubActive ? activeBg : 'transparent' }
                            _hover={{ color: activeTextColor, bg: activeBg }}
                            _active={{ bg: activeBg }}
                            onClick={ onNavLinkClick }
                            target={ isInternal ? '_self' : '_blank' }
                          >
                            <NavLinkIcon item={ si } size={ 16 }/>
                            <Text fontSize="14px" fontWeight={ 500 } lineHeight="16px">
                              { si.text }
                            </Text>
                          </Link>
                        );
                        return (
                          <Box key={ si.text } as="li" listStyleType="none" w="100%">
                            { isInternal ? (
                              <NextLink href={ si.nextRoute } passHref legacyBehavior>
                                { linkContent }
                              </NextLink>
                            ) : linkContent }
                          </Box>
                        );
                      });
                    }) }
                  </Box>
                ) }
              </Box>
            );
          }

          // 普通导航项
          const isInternal = isInternalItem(item);
          const href = isInternal ? route(item.nextRoute) : item.url;
          const isActive = isInternal && item.isActive;

          const regularContent = (
            <Link
              href={ href }
              display="flex"
              alignItems="center"
              h="48px"
              px={ 4 }
              gap="12px"
              w="100%"
              color={ isActive ? activeTextColor : textColor }
              bg={ isActive ? activeBg : 'transparent' }
              _hover={{ color: activeTextColor, bg: activeBg }}
              _active={{ bg: activeBg }}
              onClick={ onNavLinkClick }
              target={ isInternal ? '_self' : '_blank' }
            >
              <NavLinkIcon item={ item } size={ 16 }/>
              <Text fontSize="14px" fontWeight={ 500 } lineHeight="16px">
                { item.text }
              </Text>
            </Link>
          );

          return (
            <Box key={ item.text } as="li" listStyleType="none" w="100%">
              { isInternal ? (
                <NextLink href={ item.nextRoute } passHref legacyBehavior>
                  { regularContent }
                </NextLink>
              ) : regularContent }
            </Box>
          );
        }) }
      </VStack>

      { /* 底部 Mode 切换 */ }
      <Box px={ 4 } pt={ 2 } pb={ 4 }>
        <Divider borderColor={ dividerColor } mb={ 2 }/>
        <LanguageSwitcher isMobile/>
        <Flex
          h="48px"
          alignItems="center"
          justifyContent="space-between"
          cursor="pointer"
          onClick={ toggleColorMode }
          _hover={{ color: activeTextColor }}
          borderRadius="4px"
          px={ 0 }
        >
          <HStack spacing="12px">
            <IconSvg name="moon" boxSize="16px" color={ textColor }/>
            <Text fontSize="14px" fontWeight={ 500 } lineHeight="16px" color={ textColor }>
              Mode
            </Text>
          </HStack>
          <HStack spacing="8px">
            <Text fontSize="14px" fontWeight={ 500 } lineHeight="16px" color={ activeTextColor }>
              { colorMode === 'dark' ? 'Dark' : 'Light' }
            </Text>
            <IconSvg name="arrows/east-mini" boxSize={ 6 } color={ activeTextColor }/>
          </HStack>
        </Flex>
      </Box>

      <Link
        href="https://www.blockscout.com"
        target="_blank"
        rel="noopener noreferrer"
        display="flex"
        alignItems="center"
        justifyContent="center"
        columnGap={ 2 }
        alignSelf="center"
        color={ madeWithColor }
        fontSize="14px"
        marginTop="auto"
        marginBottom="40px"
        _hover={{ color: madeWithHoverColor, textDecoration: 'none' }}
      >
        <span>Made with</span>
        <IconSvg
          name={ blockscoutIconName }
          height="14px"
          width="100px"
        />
      </Link>
    </Box>
  );
};

export default NavigationMobile;
